/**
 * loginflow state machine — pure reducer, framework-
 * agnostic. Drives the canonical email-first sign-in
 * page for both admin and portal.
 *
 * # State graph
 *
 *     email → pow-solving → identifying →
 *       │                    ├── signin-running (known user)
 *       │                    ├── enroll-otp-prompt (unknown user)
 *       │                    └── recovery-redirect (recovery)
 *       │
 *       ├── enroll-otp-prompt → verifying-otp → enroll-pick-hint
 *       │                         ↑
 *       │                         └── enroll-otp-prompt (bad code)
 *       │
 *       ├── enroll-pick-hint → enroll-registering → success
 *       │
 *       └── any state → help (cancel / fatal / unsupported)
 *
 * # Design rules
 *
 *   1. State is ALWAYS a tagged union. Each variant
 *      carries only the data that variant needs. Adding a
 *      new state shape is an ADDITIVE change that doesn't
 *      silently break old switch statements — TypeScript's
 *      exhaustiveness check catches missing cases.
 *
 *   2. The reducer is pure. No I/O, no clocks, no
 *      crypto. Every action explicitly transitions. An
 *      unrecognized action for the current state returns
 *      the same state (idempotency on stray dispatches).
 *
 *   3. Side effects live in the RUNNER (runLoginflow
 *      below). The runner consumes an AuthApiClient and
 *      dispatches actions into the reducer.
 *
 *   4. Zero React imports. Each app wraps the reducer +
 *      runner in its own thin hook.
 */

import type { AccountSetupMethod } from "../account-setup/types";
import {
  CeremonyError,
  runAssertion,
  runAttestation,
  runConditionalAssertion,
  type AssertionOptionsWire,
  type AssertionResultWire,
  type AttestationOptionsWire,
  type AttestationResultWire,
} from "../webauthn-flow";
import {
  classifyCeremonyFailure,
  type AttemptedTransport,
  type CeremonyClassification,
  type CeremonyPhase,
} from "../classify-ceremony";
import type { CapabilitySnapshot, UaHint } from "../device-capabilities";
import { recordHybridFastFail, recordHybridSuccess } from "../device-capabilities";
import { reportCeremonyEvent } from "../telemetry";
import type { AuthApiClient } from "./client";
import { solvePoW } from "./pow";
import type {
  CeremonyBeginResult,
  HintPreference,
  LoginflowErrorCode,
  PoWChallenge,
  RecoveryReason,
} from "./types";

// ── States ───────────────────────────────────────────────────────────────

/**
 * Failure reasons a help state may surface. Copied from
 * the admin login-machine contract so the UI can reuse
 * the same help copy it already has.
 */
export type HelpReason = "cancelled" | "unsupported" | "failed" | "rate_limited" | "server_error";

export type LoginflowState =
  /** Initial: user is typing their email. */
  | { step: "email"; email: string; error: string | null }
  /** PoW challenge being solved on the client. */
  | { step: "pow-solving"; email: string }
  /** POST /identify in flight. */
  | { step: "identifying"; email: string }
  /** Unknown-user branch: POST /send-otp in flight, waiting for the handle. */
  | { step: "sending-otp"; email: string; tenantAttachmentPolicy?: string }
  /** Known user path: running the WebAuthn assertion.
   *  tenantAttachmentPolicy carries the tenant's WebAuthn
   *  attachment setting from /identify so the post-cancel
   *  picker can render policy-aware "no working method" copy
   *  when device capability is excluded by tenant policy. */
  | { step: "signin-running"; email: string; challenge: CeremonyBeginResult; availableTransports?: string[]; tenantAttachmentPolicy?: string; preferredTransport?: string }
  /**
   * Known user path: the WebAuthn picker was dismissed by the
   * user and the original ceremony options carried hints (server
   * biased to QR via hints=["hybrid"]). Holds onto the original
   * challenge so the user can choose to retry the QR picker OR
   * fall back to a local-device picker (hints stripped) without
   * a fresh /identify round-trip and without losing the
   * server-side ceremony session.
   *
   * availableTransports is the deduped union from /identify;
   * the transport picker uses it to capability-disable cards
   * that have no chance of working.
   *
   * tenantAttachmentPolicy is the tenant's authenticator-
   * attachment policy from /identify (Item 5 of the
   * onboarding-capability-gating plan). Empty/undefined means
   * pre-Item-5 backend OR the lookup failed; treat as "any".
   */
  | { step: "signin-cancelled"; email: string; challenge: CeremonyBeginResult; availableTransports?: string[]; tenantAttachmentPolicy?: string; preferredTransport?: string }
  /** Unknown user path: waiting for the user to enter the OTP. */
  | { step: "enroll-otp-prompt"; email: string; enrollmentHandle: string; expiresAt: string; error: string | null; attempts: number; resending?: boolean; tenantAttachmentPolicy?: string }
  /** POST /verify-otp in flight. */
  | { step: "verifying-otp"; email: string; enrollmentHandle: string; tenantAttachmentPolicy?: string }
  /** User picks platform vs phone. */
  | { step: "enroll-pick-hint"; email: string; enrollmentHandle: string; tenantAttachmentPolicy?: string }
  /** POST /register/begin then runAttestation. */
  | { step: "enroll-registering"; email: string; enrollmentHandle: string; preference: HintPreference }
  /**
   * Account-status terminal: Identify routed the caller
   * through the Recovery branch. `reason` distinguishes the
   * four sub-cases the UI renders different copy and
   * affordances for:
   *   - activation_required → "Request activation" button
   *     transitions to `requesting-activation`.
   *   - recovery_required  → "Request a fresh invitation"
   *     button transitions to `requesting-re-enrollment`.
   *   - account_suspended  → contact admin, no self-service.
   *   - account_locked_pending_recovery → contact admin,
   *     no self-service.
   */
  | { step: "account-status"; email: string; reason: RecoveryReason }
  /**
   * POST /request-activation in flight, dispatched when the
   * user clicks the "Request activation" button on the
   * account-status screen (only valid when reason was
   * activation_required).
   */
  | { step: "requesting-activation"; email: string }
  /**
   * Terminal — activation request acknowledged by the
   * backend. Backend always returns success regardless of
   * whether the email actually exists; the UI shows a
   * generic confirmation.
   */
  | { step: "activation-requested"; email: string }
  /**
   * POST /request-re-enrollment in flight, dispatched when
   * the user clicks the "Request a fresh invitation" button
   * on the account-status screen (only valid when reason was
   * recovery_required). Sibling of `requesting-activation`.
   */
  | { step: "requesting-re-enrollment"; email: string }
  /**
   * Terminal — re-enrollment request acknowledged by the
   * backend. Backend always returns success regardless of
   * whether the email actually exists; the UI shows a
   * generic "we've notified your administrator" confirmation.
   */
  | { step: "re-enrollment-requested"; email: string }
  /**
   * Terminal — OAuth early access gate. Identify was called
   * with an oauth_client_id and the backend determined the
   * caller cannot obtain a grant on the target app (known
   * user without a direct/group grant, or unknown email in
   * a workforce tenant where JIT cannot produce a grant).
   * The login page hard-navigates to /oauth/error with the
   * return_to URL rebuilt from the original `redirect`
   * search param so the user gets the "Sign in as a
   * different user" + "Return to {app}" actions.
   */
  | { step: "no-app-access"; email: string; appName: string; membershipRequestAvailable?: boolean; organizationSlug?: string }
  /** Direct passkey path: /passkey-begin in flight. */
  | { step: "passkey-begin"; email: string }
  /** Direct passkey path: modal discoverable assertion running. */
  | { step: "passkey-running"; email: string; challenge: CeremonyBeginResult }
  /**
   * Signed-in (or enrolled) — caller navigates away.
   *
   * §B: when the server's ceremony-finish response carried
   * `setup_required: true`, the success step ALSO carries
   * setupRequired + setupAvailable so the page can route into
   * the <AccountSetupInterstitial> instead of navigating to
   * the dashboard. Old backends / no-floor tenants omit the
   * fields and the caller proceeds as usual.
   */
  | {
      step: "success";
      email: string;
      /**
       * True when this success terminates a *first-time enrollment*
       * (the user just created their only passkey via the
       * email→OTP→passkey branch), as opposed to a returning-user
       * sign-in. Set ONLY by the `enroll-success` transition.
       *
       * Load-bearing for surface parity: a brand-new user who just
       * minted their sole credential gets the paced, education-rich
       * success surface (the same <PasskeyCeremony> "you're set up"
       * + recovery safety-net the admin enrollment wizard shows),
       * and the auto-navigate is deferred to their explicit
       * Continue click. A returning user keeps the ~1.2s
       * auto-advancing celebration. Absent/false ⇒ returning-user
       * fast path (unchanged behaviour for every existing surface).
       */
      enrolled?: boolean;
      setupRequired?: boolean;
      setupAvailable?: ReadonlyArray<AccountSetupMethod>;
      /**
       * RFC 3339 expiry of the session minted at ceremony
       * finish. The §B interstitial reads this to render a
       * countdown chip (D-6) so the user sees how much
       * time remains before re-sign-in is required. Always
       * populated when setupRequired is true; on the
       * happy path (setupRequired absent) the caller may
       * ignore it.
       */
      expiresAt?: string;
    }
  /**
   * Any state → help screen.
   *
   * `classification` carries the rich
   * `CeremonyClassification` produced by `classifyCeremonyFailure`
   * when the failure originated in a ceremony runner AND the
   * caller wired up the diagnostics context (see RunnerContext).
   * UI surfaces that consume this state can render the
   * canonical `<CeremonyError>` primitive when classification
   * is present, falling back to the legacy `<HelpScreen>`
   * (driven from `reason` + `message`) otherwise. The fallback
   * is load-bearing for backward-compat: orchestrators that
   * haven't opted into diagnostics still get a working help UX
   * with no churn.
   */
  | {
      step: "help";
      email: string;
      reason: HelpReason;
      message: string;
      classification?: CeremonyClassification | null;
    };

// ── Actions ──────────────────────────────────────────────────────────────

export type LoginflowAction =
  | { type: "edit-email"; email: string }
  | { type: "submit-email" }
  /** PoW solver started. */
  | { type: "pow-started" }
  /** PoW solved; moving to the identify call. */
  | { type: "identify-started" }
  /** Identify resolved to a known user. */
  | { type: "identify-known"; challenge: CeremonyBeginResult; availableTransports?: string[]; tenantAttachmentPolicy?: string; preferredTransport?: string }
  /** Identify resolved to an unknown user → enrollment path. */
  | { type: "identify-unknown"; tenantAttachmentPolicy?: string }
  /**
   * Identify routed through the Recovery branch. `reason`
   * is carried straight from the wire response so the UI
   * can render the right copy for each sub-case.
   */
  | { type: "identify-recovery"; reason: RecoveryReason }
  /**
   * Identify's OAuth early access gate denied the caller.
   * Reducer transitions to the `no-app-access` terminal;
   * the runner on the login page picks it up and
   * hard-navigates to /oauth/error.
   */
  | { type: "identify-no-app-access"; appName: string; membershipRequestAvailable?: boolean; organizationSlug?: string }
  /** User clicked "Request activation" on the account-status screen. */
  | { type: "request-activation-started" }
  /** Activation request acknowledged by the backend. */
  | { type: "activation-requested" }
  /** User clicked "Request a fresh invitation" on the account-status screen. */
  | { type: "request-re-enrollment-started" }
  /** Re-enrollment request acknowledged by the backend. */
  | { type: "re-enrollment-requested" }
  /** Identify surfaced a validation / server error. */
  | {
      type: "identify-failed";
      reason: HelpReason;
      message: string;
      classification?: CeremonyClassification | null;
    }

  /**
   * Sign-in path: ceremony success.
   *
   * §B: when the backend returns SetupRequired=true alongside
   * the bearer, the runner threads it through here so the
   * reducer lands on `success` with setupRequired set. The
   * page-level effect then routes into the
   * <AccountSetupInterstitial> instead of navigating.
   */
  | {
      type: "signin-success";
      setupRequired?: boolean;
      setupAvailable?: ReadonlyArray<AccountSetupMethod>;
      expiresAt?: string;
      /**
       * M2.1: server-resolved canonical email. When present,
       * the reducer prefers this over the user-typed value
       * (handles case-normalisation, alias resolution, and
       * keeps the typed-email path consistent with the
       * Conditional UI path's server-canonical identity).
       */
      email?: string;
    }
  /**
   * Sign-in path: ceremony failure.
   *
   * `classification` is populated by the runner when the
   * caller wired up `RunnerContext.diagnostics`. Surfaces that
   * consume the resulting `help` state render
   * `<CeremonyError>` from the classification when present
   * (richer remedy + diagnostic disclosure) and fall back to
   * `<HelpScreen>` from `reason` + `message` otherwise.
   */
  | {
      type: "signin-failed";
      reason: HelpReason;
      message: string;
      classification?: CeremonyClassification | null;
    }
  /**
   * Sign-in path: the WebAuthn picker was cancelled and the
   * runner has decided to surface the QR-vs-device chooser.
   * Carries the original challenge forward so a retry can be
   * issued without a fresh /identify call.
   */
  | { type: "signin-cancel"; challenge: CeremonyBeginResult }
  /**
   * Sign-in path: user picked "Try QR again" or "Use a passkey
   * on this device" from the chooser. The runner dispatches
   * this with the challenge to reuse — same options for QR,
   * options-without-hints for device. Reducer transitions back
   * to `signin-running` so the existing useEffect that calls
   * runSigninCeremony fires again.
   */
  | { type: "signin-retry"; challenge: CeremonyBeginResult }

  /** Enrollment: SendOTP returned a handle. */
  | { type: "resend-otp-started" }
  | { type: "otp-sent"; enrollmentHandle: string; expiresAt: string }
  /**
   * Enrollment: VerifyOTP call is in flight. Dispatched by
   * runVerifyOTP BEFORE the API call so the reducer can
   * move the state machine to `verifying-otp`, which in
   * turn unmounts the OTPForm (preventing duplicate
   * submissions) and shows the loading state. Without this
   * dispatch, the subsequent `otp-verified` arrives while
   * the state is still `enroll-otp-prompt`, its reducer
   * guard no-ops, and the UI stays stuck on the form — user
   * clicks again, fires another API call, eventually trips
   * the VerifyOTP rate limit and surfaces a 429.
   */
  | { type: "verify-otp-started" }
  /** Enrollment: VerifyOTP succeeded. */
  | { type: "otp-verified" }
  /** Enrollment: VerifyOTP failed (wrong code, etc.). */
  | { type: "otp-failed"; message: string }
  /** Enrollment: OTP exhausted — user must start over. */
  | { type: "otp-exhausted"; message: string }

  /** Enrollment: user picked "this device" / "my phone". */
  | { type: "pick-hint"; preference: HintPreference }
  /**
   * Enrollment: registration succeeded. The reducer transitions
   * straight to `success`.
   */
  | { type: "enroll-success" }
  /** Enrollment: registration failed. */
  | {
      type: "enroll-failed";
      reason: HelpReason;
      message: string;
      classification?: CeremonyClassification | null;
    }

  /** Direct passkey path: user clicked "Sign in with a passkey". */
  | { type: "start-passkey" }
  /** Direct passkey path: /passkey-begin succeeded. */
  | { type: "passkey-begin-done"; challenge: CeremonyBeginResult }
  /** Direct passkey path: /passkey-begin or assertion failed. */
  | {
      type: "passkey-failed";
      reason: HelpReason;
      message: string;
      classification?: CeremonyClassification | null;
    }
  /** Direct passkey path: discoverable assertion verified. */
  | {
      type: "passkey-signin-success";
      setupRequired?: boolean;
      setupAvailable?: ReadonlyArray<AccountSetupMethod>;
      expiresAt?: string;
      /**
       * M2.1: the authenticated user's email from the server.
       * Optional only because the action shape predates this
       * field; runners produced after 2026-05-14 always carry
       * it. When present, the reducer prefers this over the
       * carried state.email — the server's resolved value is
       * authoritative.
       */
      email?: string;
    }

  /** Restart from email state (retry after help). */
  | { type: "reset" };

// ── Reducer ──────────────────────────────────────────────────────────────

export const initialLoginflowState: LoginflowState = {
  step: "email",
  email: "",
  error: null,
};

export function loginflowReducer(state: LoginflowState, action: LoginflowAction): LoginflowState {
  switch (action.type) {
    case "edit-email":
      if (state.step !== "email") return state;
      return { ...state, email: action.email, error: null };

    case "submit-email":
      if (state.step !== "email") return state;
      if (!isValidEmail(state.email)) {
        return { ...state, error: "Please enter a valid email address." };
      }
      return { step: "pow-solving", email: state.email };

    case "pow-started":
      // Accept from email submit path only.
      return state;

    case "identify-started":
      if (state.step !== "pow-solving") return state;
      return { step: "identifying", email: state.email };

    case "identify-known":
      if (state.step !== "identifying") return state;
      return {
        step: "signin-running",
        email: state.email,
        challenge: action.challenge,
        availableTransports: action.availableTransports,
        tenantAttachmentPolicy: action.tenantAttachmentPolicy,
        // H-1 / §4.8 — server-side cross-device transport
        // preference. Carries through to signin-running and
        // signin-cancelled so any post-/identify picker mount
        // can seed its initial selection from the user's
        // personal pick instead of falling straight to the
        // tenant/device recommender.
        preferredTransport: action.preferredTransport,
      };

    case "identify-unknown":
      if (state.step !== "identifying") return state;
      // Transition to an explicit `sending-otp` state so
      // a reader (or test) can follow the state graph
      // without knowing about the runner's implicit
      // SendOTP chain. The visible UI for sending-otp is
      // the same spinner as identifying.
      // tenantAttachmentPolicy carries forward through the
      // OTP + enrollment path so the chooser at the picker
      // step can render policy-aware NoWorkingMethodScreen
      // copy if device capability and policy don't intersect.
      return { step: "sending-otp", email: state.email, tenantAttachmentPolicy: action.tenantAttachmentPolicy };

    case "identify-recovery":
      if (state.step !== "identifying") return state;
      return {
        step: "account-status",
        email: state.email,
        reason: action.reason,
      };

    case "identify-no-app-access":
      if (state.step !== "identifying") return state;
      return {
        step: "no-app-access",
        email: state.email,
        appName: action.appName,
        membershipRequestAvailable: action.membershipRequestAvailable,
        organizationSlug: action.organizationSlug,
      };

    case "request-activation-started":
      // Only valid from the activation_required branch of
      // the account-status screen. Guards against a
      // runner mis-dispatch landing us in a flow we can't
      // exit cleanly.
      if (
        state.step !== "account-status" ||
        state.reason !== "activation_required"
      ) {
        return state;
      }
      return { step: "requesting-activation", email: state.email };

    case "activation-requested":
      if (state.step !== "requesting-activation") return state;
      return { step: "activation-requested", email: state.email };

    case "request-re-enrollment-started":
      // Only valid from the recovery_required branch of the
      // account-status screen — the user is sitting on the
      // "Your passkeys were removed" terminal. Same guard
      // shape as request-activation-started.
      if (
        state.step !== "account-status" ||
        state.reason !== "recovery_required"
      ) {
        return state;
      }
      return { step: "requesting-re-enrollment", email: state.email };

    case "re-enrollment-requested":
      if (state.step !== "requesting-re-enrollment") return state;
      return { step: "re-enrollment-requested", email: state.email };

    case "identify-failed":
      return {
        step: "help",
        email: safeEmail(state),
        reason: action.reason,
        message: action.message,
        classification: action.classification ?? null,
      };

    case "signin-success":
      if (state.step !== "signin-running") return state;
      return {
        step: "success",
        // M2.1: server-canonical email when present, falling
        // back to the typed value otherwise.
        email: action.email ?? state.email,
        setupRequired: action.setupRequired,
        setupAvailable: action.setupAvailable,
        expiresAt: action.expiresAt,
      };

    case "signin-failed":
      if (state.step !== "signin-running") return state;
      return {
        step: "help",
        email: state.email,
        reason: action.reason,
        message: action.message,
        classification: action.classification ?? null,
      };

    case "signin-cancel":
      // Only meaningful when we were actually in the assertion
      // ceremony. A late-arriving cancel after the user already
      // navigated elsewhere is a no-op.
      if (state.step !== "signin-running") return state;
      return {
        step: "signin-cancelled",
        email: state.email,
        challenge: action.challenge,
        availableTransports: state.availableTransports,
        tenantAttachmentPolicy: state.tenantAttachmentPolicy,
        // Preserve H-1 server-side preference so the post-
        // cancel picker can still seed from it.
        preferredTransport: state.preferredTransport,
      };

    case "signin-retry":
      // Re-enters the assertion ceremony from the cancelled-
      // chooser screen. The runner has chosen which challenge
      // to reuse (original options vs hints-stripped clone)
      // and passed it in. availableTransports + tenant policy +
      // preferred transport survive so a subsequent cancel
      // preserves the capability, policy, and preference info.
      if (state.step !== "signin-cancelled") return state;
      return {
        step: "signin-running",
        email: state.email,
        challenge: action.challenge,
        availableTransports: state.availableTransports,
        tenantAttachmentPolicy: state.tenantAttachmentPolicy,
        preferredTransport: state.preferredTransport,
      };

    case "resend-otp-started":
      return state.step === "enroll-otp-prompt" && !state.resending ? { ...state, resending: true } : state;

    case "otp-sent":
      // Valid from sending-otp (initial send after an
      // unknown Identify), identifying (belt-and-suspenders
      // for tests that drive the action directly), or
      // enroll-otp-prompt (resend).
      if (
        state.step !== "sending-otp" &&
        state.step !== "identifying" &&
        state.step !== "enroll-otp-prompt"
      ) {
        return state;
      }
      return {
        step: "enroll-otp-prompt",
        email: safeEmail(state),
        enrollmentHandle: action.enrollmentHandle,
        expiresAt: action.expiresAt,
        error: null,
        attempts: 0,
        tenantAttachmentPolicy: extractTenantAttachmentPolicy(state),
      };

    case "verify-otp-started":
      // Only valid from the prompt. The transition unmounts
      // the OTPForm (the portal / admin pages render
      // LoadingState for `verifying-otp`) so a user who
      // clicks Verify twice in quick succession can't fire
      // a second POST — the second click has nothing to
      // click. Preserves enrollmentHandle + email so the
      // subsequent otp-verified / otp-failed transitions
      // have the context they need.
      if (state.step !== "enroll-otp-prompt" || state.resending) return state;
      return {
        step: "verifying-otp",
        email: state.email,
        enrollmentHandle: state.enrollmentHandle,
        tenantAttachmentPolicy: state.tenantAttachmentPolicy,
      };

    case "otp-failed":
      if (state.step !== "verifying-otp" && state.step !== "enroll-otp-prompt") return state;
      return {
        step: "enroll-otp-prompt",
        email: safeEmail(state),
        enrollmentHandle: "enrollmentHandle" in state ? state.enrollmentHandle : "",
        expiresAt: "expiresAt" in state ? state.expiresAt : "",
        error: action.message,
        attempts: ("attempts" in state ? state.attempts : 0) + 1,
        tenantAttachmentPolicy: extractTenantAttachmentPolicy(state),
      };

    case "otp-verified":
      if (state.step !== "verifying-otp") return state;
      return {
        step: "enroll-pick-hint",
        email: state.email,
        enrollmentHandle: state.enrollmentHandle,
        tenantAttachmentPolicy: state.tenantAttachmentPolicy,
      };

    case "otp-exhausted":
      // UX-SI audit: OTP exhaustion is a "you used up this
      // code's attempts" state, not a generic "couldn't sign
      // you in" failure. Route via `rate_limited` so the help
      // screen surfaces the Clock icon + "Too many attempts"
      // title and the user reads the right mental model.
      // The user can still recover by pressing "Try again" —
      // that resets to email entry, where re-submitting the
      // same email triggers a fresh send-OTP.
      return {
        step: "help",
        email: safeEmail(state),
        reason: "rate_limited",
        message: action.message,
      };

    case "pick-hint":
      if (state.step !== "enroll-pick-hint") return state;
      return {
        step: "enroll-registering",
        email: state.email,
        enrollmentHandle: state.enrollmentHandle,
        preference: action.preference,
      };

    case "enroll-success":
      if (state.step !== "enroll-registering") return state;
      // enrolled:true marks the first-time-enrollment terminal so
      // the surface renders the paced education-rich success
      // (parity with the admin enrollment wizard) instead of the
      // returning-user fast auto-advance.
      return { step: "success", email: state.email, enrolled: true };

    case "enroll-failed":
      if (state.step !== "enroll-registering") return state;
      return {
        step: "help",
        email: state.email,
        reason: action.reason,
        message: action.message,
        classification: action.classification ?? null,
      };

    case "start-passkey":
      if (state.step !== "email") return state;
      return { step: "passkey-begin", email: state.email };

    case "passkey-begin-done":
      if (state.step !== "passkey-begin") return state;
      return { step: "passkey-running", email: state.email, challenge: action.challenge };

    case "passkey-signin-success":
      if (state.step !== "passkey-running") return state;
      // M2.1: server-resolved email is authoritative. For the
      // Conditional UI path the user never typed an email, so
      // state.email is empty here — falling back to it would
      // leave the celebration screen showing "Signed in as "
      // with no identity. The action's email overrides when
      // present (every runner past 2026-05-14 carries it).
      return {
        step: "success",
        email: action.email ?? state.email,
        setupRequired: action.setupRequired,
        setupAvailable: action.setupAvailable,
        expiresAt: action.expiresAt,
      };

    case "passkey-failed":
      if (state.step !== "passkey-begin" && state.step !== "passkey-running") return state;
      return {
        step: "help",
        email: state.email,
        reason: action.reason,
        message: action.message,
        classification: action.classification ?? null,
      };

    case "reset":
      return { step: "email", email: safeEmail(state), error: null };
  }
}

// ── Helpers ──────────────────────────────────────────────────────────────

/**
 * safeEmail returns the `email` field of any state that
 * carries one. Used by actions that can fire from
 * multiple states (`identify-failed`, `otp-failed`,
 * `reset`) so the reducer stays exhaustive without a
 * giant conditional.
 */
function safeEmail(state: LoginflowState): string {
  if ("email" in state) return state.email;
  return "";
}

/**
 * Pull `tenantAttachmentPolicy` off whichever state shape
 * happens to carry it. The field is optional on every shape
 * that has it (server omits when the lookup fails or the
 * backend is pre-Item-5), so undefined is the legitimate
 * "unknown" value — propagated through to the picker /
 * chooser as-is, where it gets treated as "any" for
 * gating purposes. Mirrors the safeEmail helper: small,
 * shape-agnostic, total over LoginflowState.
 */
function extractTenantAttachmentPolicy(state: LoginflowState): string | undefined {
  if ("tenantAttachmentPolicy" in state) {
    return state.tenantAttachmentPolicy;
  }
  return undefined;
}

/**
 * Project the loginflow state's availableTransports into the
 * boolean classifier signal "does this user have any enrolled
 * credentials." True when /identify returned at least one
 * transport tag, false when the array is explicitly empty,
 * undefined when the state shape doesn't carry the field at
 * all (registration / pre-identify / synthetic test states).
 *
 * The undefined case is load-bearing: classifyCeremonyFailure
 * preserves its legacy behaviour (platform_unavailable for
 * fast-fail+no-Hello+this_device) when this signal is missing.
 * Only when we KNOW the user has credentials AND the device
 * can't satisfy them does the classifier route to the more
 * honest credentials_on_other_device code.
 */
function extractHasEnrolledCredentials(state: LoginflowState): boolean | undefined {
  if (!("availableTransports" in state)) return undefined;
  const at = state.availableTransports;
  if (at === undefined) return undefined;
  return at.length > 0;
}

/**
 * Minimal email validation. Intentionally permissive —
 * the backend canonicalizes + validates per RFC 5321
 * which is a much more complex regex than we want to
 * maintain here. The UI's job is to catch "obviously
 * bad" input (no @ sign, leading/trailing whitespace);
 * the server's job is the authoritative check.
 */
export function isValidEmail(s: string): boolean {
  const trimmed = s.trim();
  if (trimmed.length === 0 || trimmed.length > 254) return false;
  // Must contain an @ with non-empty local and domain
  // parts. We don't accept unicode / IDN here because
  // the backend canonicalizer lowercases and we'd want
  // the same Punycode treatment — deferring to the
  // server keeps the two sides in sync.
  const at = trimmed.indexOf("@");
  if (at <= 0 || at === trimmed.length - 1) return false;
  return true;
}

// ── Runner ───────────────────────────────────────────────────────────────

export type Dispatch = (action: LoginflowAction) => void;

/**
 * Map the loginflow's `HintPreference` (the string the
 * picker UI deals in) onto the classifier / telemetry
 * `AttemptedTransport` vocabulary. `"any"` and `undefined`
 * collapse to `undefined` because they represent "no
 * preference" — both the classifier and the wire schema
 * accept that as the "not specified" case.
 */
function transportFor(preference: HintPreference | undefined): AttemptedTransport | undefined {
  switch (preference) {
    case "other_device":
      return "other_device";
    case "this_device":
      return "this_device";
    case "security_key":
      return "security_key";
    case "any":
    case undefined:
      return undefined;
  }
}

/**
 * Classify a thrown ceremony error using the rich
 * `classifyCeremonyFailure` heuristic. Returns `null` when
 * the runner's caller didn't wire diagnostics — the runner
 * falls back to the legacy `classifyCeremonyError` code
 * path for the action's `reason` field. When diagnostics IS
 * set, this also emits a `phase=<phase> outcome=error`
 * telemetry event with the classification's code.
 */
function classifyFailure(
  ctx: RunnerContext,
  err: unknown,
  phase: CeremonyPhase,
  durationMs: number,
  preference: HintPreference | undefined,
): CeremonyClassification | null {
  const d = ctx.diagnostics;
  if (!d) return null;
  // The runner wraps DOMExceptions in CeremonyError but
  // preserves the original on `.cause`; classify against
  // the original so the heuristic sees the real
  // DOMException name and message.
  const cause = err instanceof CeremonyError ? (err.cause ?? err) : err;
  // hasEnrolledCredentials drives the disambiguation between
  // platform_unavailable (fresh user) and credentials_on_other_device
  // (returning user whose credentials are bound to a different
  // device). The signal is on the state shape — every step that
  // can render a ceremony failure has availableTransports threaded
  // through. Pre-Item-5 callers (or tests) without a state shape
  // exposing it land on undefined, which preserves legacy
  // classification behaviour (platform_unavailable wins).
  const hasEnrolledCredentials = extractHasEnrolledCredentials(ctx.state);
  const classification = classifyCeremonyFailure({
    err: cause,
    durationMs,
    phase,
    transport: transportFor(preference),
    capability: d.capability,
    hasEnrolledCredentials,
  });
  reportCeremonyEvent(
    { baseUrl: d.baseUrl, ceremonyId: d.ceremonyId },
    {
      phase,
      outcome: "error",
      code: classification.code,
      errorName: classification.domException?.name,
      errorMessage: classification.domException?.message,
      transportAttempted: transportFor(preference),
      durationMs,
      capability: d.capability,
      ua: d.ua,
    },
  );
  // Feed the learned-failure counter on the device. The classifier
  // already does the hard work of distinguishing a "fast-fail with
  // no platform authenticator" (which we ALREADY hide via the
  // platform probe) from a "fast-fail on phone path" (the
  // hybrid-unreachable signal we want here). We count exactly the
  // latter — two strikes promotes the device to "no BLE" for
  // future probeHybridFeasibility() calls and the "Use my phone"
  // card disables on subsequent picker mounts. False positives
  // are recoverable via the deep-link wizard's
  // invalidateProbeCache() (Item 3).
  if (
    classification.code === "hybrid_unreachable" &&
    transportFor(preference) === "other_device"
  ) {
    recordHybridFastFail();
  }
  return classification;
}

/**
 * Emit a fire-and-forget telemetry event when diagnostics
 * is set. Used for the "ok" / "abort" outcomes around
 * begin/finish boundaries — the runner already classifies
 * failures via `classifyFailure`.
 */
function emitTelemetry(
  ctx: RunnerContext,
  phase: CeremonyPhase,
  outcome: "ok" | "abort",
  preference: HintPreference | undefined,
  durationMs?: number,
): void {
  const d = ctx.diagnostics;
  if (!d) return;
  reportCeremonyEvent(
    { baseUrl: d.baseUrl, ceremonyId: d.ceremonyId },
    {
      phase,
      outcome,
      code: outcome === "ok" ? "ok" : "abort",
      transportAttempted: transportFor(preference),
      ...(durationMs !== undefined && { durationMs }),
      capability: d.capability,
      ua: d.ua,
    },
  );
}

export interface RunnerContext {
  state: LoginflowState;
  dispatch: Dispatch;
  client: AuthApiClient;
  /**
   * Optional AbortSignal. When fired, pending network
   * calls and PoW solving are canceled and the machine
   * returns to `email` with an error banner. The caller
   * owns the controller.
   */
  signal?: AbortSignal;
  /**
   * Optional WebAuthn transport hint. Sent with the
   * identify request so the backend can set `hints` on
   * the ceremony options. Default derivation (hybrid if
   * any stored credential carries it) happens server-side
   * when this is omitted.
   */
  hint?: string;
  /**
   * Optional OAuth client_id of the target application,
   * sourced from the original /oauth/authorize URL (via
   * /login?redirect=...). Threaded through to
   * /api/loginflow/v1/identify so the backend can
   * short-circuit to the `no-app-access` terminal state
   * BEFORE starting a ceremony when the caller holds no
   * grant on the app. Absent when the user arrived at
   * /login directly with no OAuth context.
   */
  oauthClientId?: string;
  /**
   * Optional diagnostics context. When set, the ceremony
   * runners (runSignin, runRegister, runPasskeySignin,
   * runRetrySigninWithHint) classify failures with the rich
   * `classifyCeremonyFailure` heuristic and emit fire-and-
   * forget telemetry events to /api/client-telemetry. The
   * resulting `*-failed` action carries a
   * `CeremonyClassification` so a UI consuming the help
   * state can render the canonical `<CeremonyError>`
   * primitive.
   *
   * Absent → runners stay on the legacy `classifyCeremonyError`
   * code path that maps to `HelpReason` (cancelled / unsupported
   * / failed / rate_limited / server_error). The legacy
   * `<HelpScreen>` rendering continues to work; no client
   * telemetry events fire. This preserves backward-compat for
   * callers that haven't wired the diagnostic surface yet.
   */
  diagnostics?: RunnerDiagnostics;
}

/**
 * Diagnostic context the LoginSurface (or any other
 * orchestrator) provides so the runners can produce richly-
 * classified failures and emit browser-side telemetry.
 *
 * `ceremonyId` is the per-flow client UUID, matching the
 * `X-Ceremony-ID` header the AuthApiClient stamps on every
 * /api/loginflow/v1/* and /api/ceremony/v1/* call. The same
 * value is included in every telemetry event POSTed from the
 * runners.
 *
 * `capability` and `ua` are the preflight-time browser
 * capability snapshot and user-agent hint; both flow into
 * the classifier (capability) and the telemetry payload
 * (both).
 *
 * `baseUrl` is the API origin for the telemetry endpoint
 * (e.g. https://tenant.us.example.com); the reporter
 * appends `/api/client-telemetry` to it.
 */
export interface RunnerDiagnostics {
  ceremonyId: string;
  capability: CapabilitySnapshot;
  ua: UaHint;
  baseUrl: string;
}

/**
 * runSubmitEmail handles the full "submit email" flow:
 * PoW → Identify → (ceremony or SendOTP). Dispatches
 * intermediate state transitions as it progresses.
 * Returns when the machine reaches a user-visible state
 * (signin-running, enroll-otp-prompt, recovery, help).
 *
 * Each sub-path is factored into its own helper below so
 * the main flow stays readable and the unit tests can
 * drive the pieces independently.
 */
export async function runSubmitEmail(ctx: RunnerContext): Promise<void> {
  if (ctx.state.step !== "pow-solving") return;
  const email = ctx.state.email;

  // 1. PoW challenge.
  let challenge: PoWChallenge;
  try {
    challenge = await ctx.client.issuePoWChallenge();
  } catch (err) {
    ctx.dispatch({
      type: "identify-failed",
      reason: "server_error",
      message: errMessage(err, "Couldn't reach the server."),
    });
    return;
  }

  // 2. Solve the PoW. No state transition here — we stay
  // in `pow-solving` during the solve so the UI shows a
  // "solving proof-of-work" spinner.
  let nonce: string;
  try {
    const solved = await solvePoW(
      { challenge: challenge.challenge, difficulty: challenge.difficulty },
      { signal: ctx.signal },
    );
    nonce = solved.nonce;
  } catch (err) {
    ctx.dispatch({
      type: "identify-failed",
      reason: "failed",
      message: errMessage(err, "Couldn't complete the proof-of-work check."),
    });
    return;
  }

  // 3. Identify with the solved PoW.
  ctx.dispatch({ type: "identify-started" });
  let identifyResult;
  try {
    identifyResult = await ctx.client.identify({
      email,
      pow_challenge_id: challenge.challenge_id,
      pow_nonce: nonce,
      hint: ctx.hint,
      oauth_client_id: ctx.oauthClientId,
    });
  } catch (err) {
    ctx.dispatch({
      type: "identify-failed",
      reason: classifyApiError(err),
      message: errMessage(err, "Couldn't identify your account."),
    });
    return;
  }

  // Tenant policy projection (Item 5 of the onboarding-
  // capability-gating plan). May be absent on pre-Item-5
  // backends; downstream consumers treat undefined as "any".
  const tenantAttachmentPolicy = identifyResult.tenant?.attachment_policy;

  // 4. Branch on the discriminated union.
  if (identifyResult.known) {
    ctx.dispatch({
      type: "identify-known",
      challenge: identifyResult.known.challenge,
      availableTransports: identifyResult.known.available_transports,
      tenantAttachmentPolicy,
      // H-1 / §4.8 — surface the user's recorded cross-device
      // sign-in preference through to the post-/identify state
      // shape so any picker that mounts later can seed its
      // initial selection from the user's personal pick.
      preferredTransport: identifyResult.known.preferred_transport,
    });
    return;
  }
  if (identifyResult.recovery) {
    ctx.dispatch({
      type: "identify-recovery",
      reason: identifyResult.recovery.reason,
    });
    return;
  }
  if (identifyResult.no_app_access) {
    ctx.dispatch({
      type: "identify-no-app-access",
      appName: identifyResult.no_app_access.app_name,
      membershipRequestAvailable: identifyResult.no_app_access.membership_request_available,
      organizationSlug: identifyResult.no_app_access.organization_slug,
    });
    return;
  }

  if (identifyResult.unknown) {
    // Unknown user → proceed to SendOTP immediately.
    // The UI stays on a spinner (identifying) through
    // this call, then transitions to otp-prompt. The
    // tenant policy threads through via dispatch so the
    // enrollment chooser at enroll-pick-hint can read it.
    ctx.dispatch({ type: "identify-unknown", tenantAttachmentPolicy });
    await runSendOTP(ctx, email);
    return;
  }

  // Shouldn't happen — server returned an IdentifyResult
  // with no fields set. Treat as a server error.
  ctx.dispatch({
    type: "identify-failed",
    reason: "server_error",
    message: "Server returned an empty identify result.",
  });
}

/**
 * runSendOTP issues a PoW + calls /send-otp. Used both
 * by the initial "unknown user" path and by the user's
 * explicit "resend code" click on the OTP prompt.
 */
export async function runSendOTP(ctx: RunnerContext, email: string): Promise<void> {
  if (ctx.signal?.aborted) return;
  let challenge: PoWChallenge;
  try {
    challenge = await ctx.client.issuePoWChallenge();
  } catch (err) {
    if (ctx.signal?.aborted) return;
    ctx.dispatch({
      type: "identify-failed",
      reason: "server_error",
      message: errMessage(err, "Couldn't request a new code."),
    });
    return;
  }

  let nonce: string;
  try {
    const solved = await solvePoW(
      { challenge: challenge.challenge, difficulty: challenge.difficulty },
      { signal: ctx.signal },
    );
    nonce = solved.nonce;
  } catch (err) {
    if (ctx.signal?.aborted) return;
    ctx.dispatch({
      type: "identify-failed",
      reason: "failed",
      message: errMessage(err, "Couldn't complete the proof-of-work check."),
    });
    return;
  }

  try {
    if (ctx.signal?.aborted) return;
    const result = await ctx.client.sendOTP({
      email,
      pow_challenge_id: challenge.challenge_id,
      pow_nonce: nonce,
    });
    if (ctx.signal?.aborted) return;
    ctx.dispatch({
      type: "otp-sent",
      enrollmentHandle: result.enrollment_handle,
      expiresAt: result.expires_at,
    });
  } catch (err) {
    if (ctx.signal?.aborted) return;
    ctx.dispatch({
      type: "identify-failed",
      reason: classifyApiError(err),
      message: errMessage(err, "Couldn't send your verification code."),
    });
  }
}

/**
 * runVerifyOTP POSTs the code and dispatches the result.
 * Called when the user submits the OTP form.
 *
 * The `verify-otp-started` dispatch BEFORE the API call is
 * load-bearing: it moves the machine into `verifying-otp`,
 * which unmounts OTPForm at the UI layer. Without it, the
 * OTPForm stays mounted during the call, the user can
 * double-click Verify, each click fires another POST, the
 * server trips the per-handle rate limit, and the caller
 * sees 429s while the UI appears frozen. Additionally, the
 * reducer's `otp-verified` guard requires state to be
 * `verifying-otp` — dispatching on the stale
 * `enroll-otp-prompt` state would no-op and strand the
 * machine there even on a successful backend call.
 */
export async function runVerifyOTP(ctx: RunnerContext, code: string): Promise<void> {
  if (ctx.state.step !== "enroll-otp-prompt" || ctx.state.resending || ctx.signal?.aborted) return;
  const { enrollmentHandle } = ctx.state;
  ctx.dispatch({ type: "verify-otp-started" });

  try {
    await ctx.client.verifyOTP({ enrollment_handle: enrollmentHandle, code });
    ctx.dispatch({ type: "otp-verified" });
  } catch (err) {
    const wireCode = classifyLoginflowError(err);
    // Explicit dispatch per known error code. Unknown /
    // null wire codes are treated as "unknown failure"
    // and routed to the help screen — we deliberately do
    // NOT default to otp-failed, which would tell the
    // user "wrong code, try again" when the real problem
    // could be anything (rate limit, server error,
    // malformed response).
    switch (wireCode) {
      case "otp_exhausted":
        ctx.dispatch({
          type: "otp-exhausted",
          // Pairs with the help-screen Clock-icon title
          // "Too many attempts" (reason: rate_limited). The
          // body explains the recovery path — "Try again"
          // resets to email entry, where re-submitting the
          // email triggers a fresh code.
          message: errMessage(err, "You've used up the codes we sent for this email. Try again to receive a new one."),
        });
        return;
      case "otp_invalid":
        ctx.dispatch({
          type: "otp-failed",
          // UX-SI audit: remind users of the code source. The
          // bare "That code is not valid" leaves new users
          // unsure whether to refresh their inbox, request a
          // new code, or try a different value. Naming the
          // email reduces the friction on the most-error-
          // prone step in the funnel.
          message: errMessage(err, "That code isn't right. Check your email for the latest code and try again."),
        });
        return;
      case "rate_limited":
        ctx.dispatch({
          type: "enroll-failed",
          reason: "rate_limited",
          message: errMessage(err, "Too many attempts; try again shortly."),
        });
        return;
      default:
        ctx.dispatch({
          type: "enroll-failed",
          reason: classifyApiError(err),
          message: errMessage(err, "We couldn't verify your code."),
        });
        return;
    }
  }
}

/**
 * runSignin drives the WebAuthn assertion for a known
 * user: it uses the ceremony challenge already returned
 * by Identify and calls the ceremony Finish endpoint on
 * success. The state machine stays in `signin-running`
 * for the duration.
 *
 * Cancel handling is asymmetric:
 *
 *   - If the user cancels and the ceremony options carried
 *     `hints` (server biased the picker — typically
 *     hints=["hybrid"] for an all-QR-registered user), the
 *     runner dispatches `signin-cancel` so the UI can offer
 *     a "use a passkey on this device" fallback. This
 *     reuses the existing challenge — the server-side
 *     ceremony session has not been consumed because the
 *     Finish endpoint was never called.
 *
 *   - If the cancel happened on a no-hints ceremony, the
 *     normal `signin-failed → help` transition fires. There
 *     is no useful fallback to offer — the browser already
 *     showed the full picker.
 */
export async function runSignin(ctx: RunnerContext): Promise<void> {
  if (ctx.signal?.aborted) return;
  if (ctx.state.step !== "signin-running") return;
  const { challenge } = ctx.state;
  // Infer the transport from the challenge's existing hints
  // so telemetry + classifier see the right attempted-
  // transport label without the caller having to thread it
  // through state. QR-biased challenges (hints=["hybrid"])
  // record as "other_device"; client-device biases record as
  // "this_device"; bare challenges record as undefined.
  const inferredPref: HintPreference | undefined = preferenceFromChallenge(challenge);
  const start = nowMs();
  emitTelemetry(ctx, "begin", "ok", inferredPref);

  // §B gate signals captured from the ceremonyFinish response.
  // Hoisted to the surrounding scope so the post-runAssertion
  // dispatch can thread them onto signin-success. Default
  // false / undefined preserves old-backend behavior.
  let capturedSetupRequired = false;
  let capturedSetupAvailable: ReadonlyArray<AccountSetupMethod> | undefined;
  let capturedExpiresAt: string | undefined;
  // M2.1: server-canonical email captured so the success
  // dispatch can thread it onto signin-success.
  let capturedEmail: string | undefined;

  try {
    await runAssertion(
      () => {}, // no intermediate state dispatches — we own the outer state
      async () => challenge.options as AssertionOptionsWire,
      async (signed: AssertionResultWire) => {
        if (ctx.signal?.aborted) return;
        const finishResult = await ctx.client.ceremonyFinish({
          session_id: challenge.session_id,
          response: signed,
        });
        capturedSetupRequired = finishResult.setup_required === true;
        capturedSetupAvailable = finishResult.setup_available;
        capturedExpiresAt = finishResult.expires_at;
        capturedEmail = finishResult.email;
      },
      ctx.signal,
    );
    if (ctx.signal?.aborted) return;
    emitTelemetry(ctx, "finish", "ok", inferredPref, nowMs() - start);
    // Successful phone-path ceremony proves the BLE pairing CAN
    // succeed on this device — clear the learned-failure counter
    // so a single transient hiccup followed by a real success
    // doesn't leave the user one strike away from a hard block.
    if (inferredPref === "other_device") {
      recordHybridSuccess();
    }
    ctx.dispatch({
      type: "signin-success",
      setupRequired: capturedSetupRequired,
      setupAvailable: capturedSetupAvailable,
      expiresAt: capturedExpiresAt,
      email: capturedEmail,
    });
  } catch (err) {
    if (ctx.signal?.aborted) return;
    const durationMs = nowMs() - start;
    const reason = classifyCeremonyError(err);
    if (reason === "cancelled" && challengeIsQRBiased(challenge)) {
      // Soft-cancel path: emit telemetry as "abort" rather
      // than "error" so dashboards distinguish the user-
      // dismissed-the-QR scenario from genuine failures.
      // The chooser screen will surface, and the operator
      // sees the cancel cleanly without a help-screen line.
      emitTelemetry(ctx, "get", "abort", inferredPref, durationMs);
      ctx.dispatch({ type: "signin-cancel", challenge });
      return;
    }
    const classification = classifyFailure(ctx, err, "get", durationMs, inferredPref);
    ctx.dispatch({
      type: "signin-failed",
      reason,
      message: errMessage(err, "Sign-in failed."),
      classification,
    });
  }
}

/** Reverse-map a challenge's hints (set by the server or by
 *  applyHintPreferenceToChallenge) onto the loginflow's
 *  HintPreference vocabulary. Used by runSignin to infer the
 *  attempted transport for telemetry + classifier without
 *  requiring the caller to thread it explicitly. */
function preferenceFromChallenge(challenge: CeremonyBeginResult): HintPreference | undefined {
  const opts = challenge.options as { hints?: string[] };
  if (!Array.isArray(opts.hints) || opts.hints.length === 0) return undefined;
  if (opts.hints.includes("hybrid")) return "other_device";
  if (opts.hints.includes("client-device")) return "this_device";
  if (opts.hints.includes("security-key")) return "security_key";
  return undefined;
}

/** Wall-clock ms accessor with a SSR-safe fallback. Date.now
 *  is universally available; performance.now would be more
 *  precise but ~1ms is plenty for the FAST_FAIL_THRESHOLD_MS
 *  heuristic and Date is portable into every test env. */
function nowMs(): number {
  return Date.now();
}

/**
 * runRetrySigninWithHint re-enters the assertion ceremony
 * from the cancelled-chooser or help-screen surface with the
 * caller-supplied transport preference. Single canonical
 * runner for all transport-switching retries.
 *
 * The session_id is unchanged so server-side Finish accepts
 * the resulting assertion the same way — `hints` is purely
 * a UA picker bias and is not part of the signed challenge.
 *
 * Preference → WebAuthn hints mapping:
 *
 *   "other_device"  → hints=["hybrid"]
 *                     QR-first; pairs over BLE with any phone
 *                     that holds the passkey (yours, anyone
 *                     else's). The hero scenario for
 *                     "colleague's laptop, my phone".
 *
 *   "this_device"   → hints=["client-device"]
 *                     Browser surfaces local platform
 *                     authenticators + any passkey synced to
 *                     this browser via a credential manager
 *                     (iCloud / Google PM / etc.).
 *
 *   "security_key"  → hints=["security-key"]
 *                     USB / NFC / BLE roaming key dialog.
 *
 *   "any"           → hints stripped
 *                     No bias — browser picks based on
 *                     `allowCredentials.transports` alone.
 *                     Useful when the operator wants the most
 *                     permissive picker.
 *
 * NOTE: this only dispatches the state transition. Re-
 * running the WebAuthn assertion is the React layer's job —
 * the existing useEffect that watches for `signin-running`
 * calls runSigninCeremony, which reads the new (post-
 * dispatch) state with the right challenge. Calling runSignin
 * here too would race the useEffect and fire the ceremony
 * twice.
 */
export async function runRetrySigninWithHint(
  ctx: RunnerContext,
  preference: HintPreference,
): Promise<void> {
  if (ctx.state.step !== "signin-cancelled") return;
  const reused = applyHintPreferenceToChallenge(ctx.state.challenge, preference);
  ctx.dispatch({ type: "signin-retry", challenge: reused });
}

/**
 * runRetrySigninWithQR — thin shim over runRetrySigninWithHint.
 * @deprecated use runRetrySigninWithHint("other_device").
 */
export async function runRetrySigninWithQR(ctx: RunnerContext): Promise<void> {
  await runRetrySigninWithHint(ctx, "other_device");
}

/**
 * runRetrySigninWithLocal — thin shim over runRetrySigninWithHint.
 * @deprecated use runRetrySigninWithHint("this_device").
 *
 * The legacy "Local" semantic was "strip hints". The new
 * shim preserves THAT behaviour by mapping to "any" rather
 * than "this_device", so any existing caller that depended on
 * the broader picker keeps working. New code should specify
 * the preference explicitly.
 */
export async function runRetrySigninWithLocal(ctx: RunnerContext): Promise<void> {
  await runRetrySigninWithHint(ctx, "any");
}

// challengeIsQRBiased reports whether the assertion options
// carry hints=["hybrid"]. Used by the signin runner to decide
// whether a ceremony cancellation should surface the picker
// (broaden in M3 to also fire on non-QR cancels once capability
// data lets us show meaningful alternatives).
function challengeIsQRBiased(challenge: CeremonyBeginResult): boolean {
  const opts = challenge.options as { hints?: string[] };
  if (!Array.isArray(opts.hints)) return false;
  return opts.hints.includes("hybrid");
}

// applyHintPreferenceToChallenge returns a shallow copy of the
// ceremony challenge with options.hints rewritten to match the
// caller's transport preference. We do NOT mutate the original
// challenge — the original is what subsequent retries with
// other preferences may want to reuse.
function applyHintPreferenceToChallenge(
  challenge: CeremonyBeginResult,
  preference: HintPreference,
): CeremonyBeginResult {
  const optsCopy: Record<string, unknown> = { ...(challenge.options as Record<string, unknown>) };
  switch (preference) {
    case "other_device":
      optsCopy.hints = ["hybrid"];
      break;
    case "this_device":
      optsCopy.hints = ["client-device"];
      break;
    case "security_key":
      optsCopy.hints = ["security-key"];
      break;
    case "any":
    default:
      delete optsCopy.hints;
      break;
  }
  return { ...challenge, options: optsCopy };
}

/**
 * runRegister drives the enrollment WebAuthn attestation
 * after the user picked a HintPreference. POSTs
 * /register/begin to get the challenge, runs
 * runAttestation, and POSTs /register/finish.
 */
export async function runRegister(ctx: RunnerContext, email: string, displayName: string): Promise<void> {
  if (ctx.signal?.aborted) return;
  if (ctx.state.step !== "enroll-registering") return;
  const { enrollmentHandle, preference } = ctx.state;
  const beginStart = nowMs();
  emitTelemetry(ctx, "begin", "ok", preference);

  let beginResult;
  try {
    beginResult = await ctx.client.registerBegin({
      enrollment_handle: enrollmentHandle,
      email,
      display_name: displayName,
      hint_preference: preference,
    });
  } catch (err) {
    if (ctx.signal?.aborted) return;
    const classification = classifyFailure(ctx, err, "begin", nowMs() - beginStart, preference);
    ctx.dispatch({
      type: "enroll-failed",
      reason: classifyApiError(err),
      message: errMessage(err, "Couldn't start enrollment."),
      classification,
    });
    return;
  }

  if (ctx.signal?.aborted) return;
  const ceremonyStart = nowMs();
  try {
    await runAttestation(
      () => {},
      async () => beginResult.challenge.options as AttestationOptionsWire,
      async (signed: AttestationResultWire) => {
        if (ctx.signal?.aborted) return;
        await ctx.client.registerFinish({
          enrollment_handle: enrollmentHandle,
          session_id: beginResult.challenge.session_id,
          response: signed,
        });
      },
      ctx.signal,
    );
    if (ctx.signal?.aborted) return;
    emitTelemetry(ctx, "finish", "ok", preference, nowMs() - ceremonyStart);
    ctx.dispatch({ type: "enroll-success" });
  } catch (err) {
    if (ctx.signal?.aborted) return;
    const classification = classifyFailure(
      ctx,
      err,
      "create",
      nowMs() - ceremonyStart,
      preference,
    );
    ctx.dispatch({
      type: "enroll-failed",
      reason: classifyCeremonyError(err),
      message: errMessage(err, "Enrollment failed."),
      classification,
    });
  }
}

/**
 * runPasskeySignin drives the "Sign in with a passkey" button flow
 * (Path C). Calls /passkey-begin to get a discoverable challenge,
 * then runs a modal assertion, then calls ceremonyFinish.
 */
export async function runPasskeySignin(ctx: RunnerContext): Promise<void> {
  if (ctx.signal?.aborted) return;
  if (ctx.state.step !== "passkey-begin") return;
  // Direct-passkey flow has no user-picked transport — the
  // browser surfaces the discoverable picker and the user chooses
  // there. Telemetry records the transport as unspecified
  // (undefined): there was no caller bias. The `phase` field
  // ("begin") already distinguishes this real attempt from a
  // preflight no-transport event, so no pseudo-transport value is
  // needed (P2-2 removed the old `conditional` member).
  const transport: AttemptedTransport | undefined = undefined;
  const beginStart = nowMs();
  emitTelemetry(ctx, "begin", "ok", undefined);

  let beginResult;
  try {
    beginResult = await ctx.client.passkeyBegin({ hint: ctx.hint });
  } catch (err) {
    if (ctx.signal?.aborted) return;
    const classification = classifyFailure(ctx, err, "begin", nowMs() - beginStart, undefined);
    ctx.dispatch({
      type: "passkey-failed",
      reason: classifyApiError(err),
      message: errMessage(err, "Couldn't start passkey sign-in."),
      classification,
    });
    return;
  }

  ctx.dispatch({ type: "passkey-begin-done", challenge: beginResult.challenge });

  if (ctx.signal?.aborted) return;
  const ceremonyStart = nowMs();
  // §B gate signals captured for the direct-passkey path,
  // same shape as runSignin above.
  let capturedSetupRequired = false;
  let capturedSetupAvailable: ReadonlyArray<AccountSetupMethod> | undefined;
  let capturedExpiresAt: string | undefined;
  // M2.1: server-resolved email — authoritative for the
  // success state's identity display. Empty string is the
  // canonical "this user has no primary email" value.
  let capturedEmail: string | undefined;
  try {
    await runAssertion(
      () => {},
      async () => beginResult.challenge.options as AssertionOptionsWire,
      async (signed: AssertionResultWire) => {
        if (ctx.signal?.aborted) return;
        const finishResult = await ctx.client.ceremonyFinish({
          session_id: beginResult.challenge.session_id,
          response: signed,
        });
        capturedSetupRequired = finishResult.setup_required === true;
        capturedSetupAvailable = finishResult.setup_available;
        capturedExpiresAt = finishResult.expires_at;
        capturedEmail = finishResult.email;
      },
      ctx.signal,
    );
    if (ctx.signal?.aborted) return;
    emitTelemetry(ctx, "finish", "ok", undefined, nowMs() - ceremonyStart);
    ctx.dispatch({
      type: "passkey-signin-success",
      setupRequired: capturedSetupRequired,
      setupAvailable: capturedSetupAvailable,
      expiresAt: capturedExpiresAt,
      email: capturedEmail,
    });
  } catch (err) {
    if (ctx.signal?.aborted) return;
    // Discoverable assertion: no caller-picked transport, so the
    // classifier sees `transport: undefined` (set at the top of
    // this runner). NotAllowedError disambiguation only special-
    // cases this_device / other_device; undefined falls through to
    // the general case — exactly as the old "conditional" value
    // did (it matched neither branch). preferenceFromChallenge
    // would also return undefined here (hints-stripped challenge).
    const d = ctx.diagnostics;
    let classification: CeremonyClassification | null = null;
    if (d) {
      const cause = err instanceof CeremonyError ? (err.cause ?? err) : err;
      classification = classifyCeremonyFailure({
        err: cause,
        durationMs: nowMs() - ceremonyStart,
        phase: "get",
        transport,
        capability: d.capability,
      });
      reportCeremonyEvent(
        { baseUrl: d.baseUrl, ceremonyId: d.ceremonyId },
        {
          phase: "get",
          outcome: "error",
          code: classification.code,
          errorName: classification.domException?.name,
          errorMessage: classification.domException?.message,
          transportAttempted: transport,
          durationMs: nowMs() - ceremonyStart,
          capability: d.capability,
          ua: d.ua,
        },
      );
    }
    ctx.dispatch({
      type: "passkey-failed",
      reason: classifyCeremonyError(err),
      message: errMessage(err, "Passkey sign-in failed."),
      classification,
    });
  }
}

/**
 * runConditionalPasskeySignin drives the WebAuthn Conditional UI
 * (autofill) sign-in path. The state machine transitions match
 * `runPasskeySignin` but the underlying ceremony differs:
 *
 *   - `runConditionalAssertion` calls
 *     `navigator.credentials.get({ mediation: 'conditional', ... })`,
 *     which surfaces matching passkeys as an autofill suggestion
 *     ON the email input rather than as a modal picker. The user
 *     keeps full agency over whether to engage the autofill.
 *
 *   - Per W3C spec, conditional ceremonies must fail SILENTLY —
 *     all error cases return null, and the runner exits without
 *     dispatching `passkey-failed`. This preserves the privacy
 *     guarantee that a conditional request cannot be used to
 *     probe whether credentials exist for a given site.
 *
 *   - The caller MUST supply an AbortSignal. The conditional
 *     get() and a modal get() cannot coexist (one cancels the
 *     other), so the caller is required to abort this runner
 *     before kicking off the typed-email modal flow.
 *
 * Telemetry: emitted only on the begin phase (the
 * conditional-finish surface needs the discoverable-finish
 * code path), and only on actual selection — silent aborts /
 * silent failures are not telemetry-worthy by design.
 */
export async function runConditionalPasskeySignin(
  ctx: RunnerContext,
  signal: AbortSignal,
): Promise<void> {
  if (ctx.state.step !== "email" || ctx.state.email !== "" || signal.aborted || ctx.signal?.aborted) return;

  // Fetch the discoverable challenge from /passkey-begin. Use
  // the same endpoint as the modal flow — only the mediation
  // mode differs at the navigator.credentials.get layer. If the
  // backend rejects (e.g., rate limit, tenant policy), exit
  // silently — per W3C spec, conditional errors must not be
  // observable.
  let beginResult;
  try {
    beginResult = await ctx.client.passkeyBegin({ hint: ctx.hint });
  } catch {
    // Quiet exit — let the typed-email path take over when the
    // user clicks Continue. The conditional get() never starts.
    return;
  }

  // If the caller already aborted (e.g., user clicked Continue
  // before the begin call returned), exit cleanly.
  if (signal.aborted || ctx.signal?.aborted) {
    return;
  }


  const ceremonyStart = nowMs();
  const wire = beginResult.challenge.options as AssertionOptionsWire;
  const signed = await runConditionalAssertion(wire, signal);

  // signed === null is the canonical conditional-failure /
  // conditional-abort outcome. The browser surfaced the autofill
  // hint (or didn't), the user either declined or aborted, and
  // we exit quietly. No state transition; the email step still
  // owns the surface.
  if (!signed || signal.aborted || ctx.signal?.aborted) return;

  // Keep the email form available until the user actually selects a credential.
  ctx.dispatch({ type: "start-passkey" });
  ctx.dispatch({ type: "passkey-begin-done", challenge: beginResult.challenge });

  // Conditional get() resolved — the user picked a passkey via
  // autofill. From here the flow is identical to the modal
  // discoverable path: post the assertion to ceremony-finish,
  // dispatch success.
  // Assigned from the ceremony-finish result below; the catch path returns,
  // so an initializer here would only ever be overwritten before any read.
  let setupRequired: boolean;
  let setupAvailable: ReadonlyArray<AccountSetupMethod> | undefined;
  let expiresAt: string | undefined;
  // M2.1: server-resolved email — this is THE field that makes
  // the conditional success surface usable. The client never
  // typed an email (Conditional UI picked the credential via
  // autofill), so this server-returned identity is the only
  // way the celebration screen can show who just signed in.
  let email: string | undefined;
  try {
    const finishResult = await ctx.client.ceremonyFinish({
      session_id: beginResult.challenge.session_id,
      response: signed,
    });
    setupRequired = finishResult.setup_required === true;
    setupAvailable = finishResult.setup_available;
    expiresAt = finishResult.expires_at;
    email = finishResult.email;
  } catch (err) {
    if (signal.aborted || ctx.signal?.aborted) return;
    // Conditional-path errors AFTER the user picked a passkey
    // are NOT silent — the user made an explicit choice and
    // deserves an error explanation. Route through the same
    // help-screen the modal path uses.
    const d = ctx.diagnostics;
    let classification: CeremonyClassification | null = null;
    if (d) {
      const cause = err instanceof CeremonyError ? (err.cause ?? err) : err;
      classification = classifyCeremonyFailure({
        err: cause,
        durationMs: nowMs() - ceremonyStart,
        phase: "finish",
        // Conditional/autofill mediation: the browser surfaced the
        // credentials, the user picked no explicit transport, so
        // the transport slot is unspecified (P2-2 removed the old
        // `conditional` pseudo-transport). Autofill mediation is
        // captured separately via the conditional_mediation flag.
        transport: undefined,
        capability: d.capability,
      });
    }
    ctx.dispatch({
      type: "passkey-failed",
      reason: classifyCeremonyError(err),
      message: errMessage(err, "Couldn't complete sign-in."),
      classification,
    });
    return;
  }

  if (signal.aborted || ctx.signal?.aborted) return;
  ctx.dispatch({
    type: "passkey-signin-success",
    setupRequired,
    setupAvailable,
    expiresAt,
    email,
  });
}

/**
 * runRequestActivation issues a PoW + calls /request-activation.
 * Invoked when the user clicks the "Request activation"
 * button on the account-status screen (activation_required
 * reason only). The backend always returns success
 * regardless of whether the email is eligible, so the UI
 * transitions to `activation-requested` on any non-thrown
 * response; errors bubble up to the help screen.
 *
 * Dispatches `request-activation-started` BEFORE the API
 * call so the reducer locks the button (same double-submit
 * protection the verify-otp path uses).
 */
export async function runRequestActivation(ctx: RunnerContext): Promise<void> {
  if (
    ctx.state.step !== "account-status" ||
    ctx.state.reason !== "activation_required"
  ) {
    return;
  }
  const email = ctx.state.email;

  // PoW first so each rate-limit bucket increment costs
  // CPU. Same posture as runSendOTP.
  let challenge: PoWChallenge;
  try {
    challenge = await ctx.client.issuePoWChallenge();
  } catch (err) {
    ctx.dispatch({
      type: "identify-failed",
      reason: classifyApiError(err),
      message: errMessage(err, "Couldn't start activation request."),
    });
    return;
  }
  let nonce: string;
  try {
    const solved = await solvePoW(
      { challenge: challenge.challenge, difficulty: challenge.difficulty },
      { signal: ctx.signal },
    );
    nonce = solved.nonce;
  } catch (err) {
    ctx.dispatch({
      type: "identify-failed",
      reason: "failed",
      message: errMessage(err, "Couldn't complete the proof-of-work check."),
    });
    return;
  }

  // Dispatch the transition BEFORE the network call so a
  // double-click cannot re-fire the button while the POST
  // is in flight. Same pattern as verify-otp-started.
  ctx.dispatch({ type: "request-activation-started" });

  try {
    await ctx.client.requestActivation({
      email,
      pow_challenge_id: challenge.challenge_id,
      pow_nonce: nonce,
    });
    ctx.dispatch({ type: "activation-requested" });
  } catch (err) {
    ctx.dispatch({
      type: "identify-failed",
      reason: classifyApiError(err),
      message: errMessage(err, "Couldn't submit activation request."),
    });
  }
}

/**
 * runRequestReEnrollment is the sibling of runRequestActivation.
 * Issues a PoW + calls /request-re-enrollment when the user
 * clicks "Request a fresh invitation" on the account-status
 * screen rendered for the `recovery_required` reason (their
 * passkeys were all removed). Same enumeration-safe wire
 * posture as runRequestActivation; the backend always returns
 * success regardless of whether the email is eligible, so we
 * transition to `re-enrollment-requested` on any non-thrown
 * response.
 *
 * Dispatches `request-re-enrollment-started` BEFORE the API
 * call so the reducer locks the button (same double-submit
 * protection runRequestActivation uses).
 */
export async function runRequestReEnrollment(ctx: RunnerContext): Promise<void> {
  if (
    ctx.state.step !== "account-status" ||
    ctx.state.reason !== "recovery_required"
  ) {
    return;
  }
  const email = ctx.state.email;

  let challenge: PoWChallenge;
  try {
    challenge = await ctx.client.issuePoWChallenge();
  } catch (err) {
    ctx.dispatch({
      type: "identify-failed",
      reason: classifyApiError(err),
      message: errMessage(err, "Couldn't start re-enrollment request."),
    });
    return;
  }
  let nonce: string;
  try {
    const solved = await solvePoW(
      { challenge: challenge.challenge, difficulty: challenge.difficulty },
      { signal: ctx.signal },
    );
    nonce = solved.nonce;
  } catch (err) {
    ctx.dispatch({
      type: "identify-failed",
      reason: "failed",
      message: errMessage(err, "Couldn't complete the proof-of-work check."),
    });
    return;
  }

  ctx.dispatch({ type: "request-re-enrollment-started" });

  try {
    await ctx.client.requestReEnrollment({
      email,
      pow_challenge_id: challenge.challenge_id,
      pow_nonce: nonce,
    });
    ctx.dispatch({ type: "re-enrollment-requested" });
  } catch (err) {
    ctx.dispatch({
      type: "identify-failed",
      reason: classifyApiError(err),
      message: errMessage(err, "Couldn't submit re-enrollment request."),
    });
  }
}

// ── Error classification ─────────────────────────────────────────────────

/**
 * Map an unknown error (from a failed API call) to a
 * HelpReason so the UI can pick the right copy. The app-
 * specific api-client normalizes HTTP non-2xx into
 * ApiError subclasses; we duck-type on the `.status`
 * field to avoid coupling auth-core to a specific
 * ApiError class.
 */
function classifyApiError(err: unknown): HelpReason {
  if (err && typeof err === "object" && "status" in err) {
    const status = (err as { status: number }).status;
    if (status === 429) return "rate_limited";
    if (status >= 500) return "server_error";
  }
  return "failed";
}

/**
 * Pull the loginflow error code ("otp_invalid",
 * "otp_exhausted", etc.) out of the app's normalized
 * ApiError body. The exact shape of that body is
 * app-specific, but every one we've seen carries an
 * `error` field with the wire code.
 */
function classifyLoginflowError(err: unknown): LoginflowErrorCode | null {
  if (err && typeof err === "object" && "body" in err) {
    const body = (err as { body: unknown }).body;
    if (body && typeof body === "object" && "error" in body) {
      const code = (body as { error: unknown }).error;
      if (typeof code === "string") return code as LoginflowErrorCode;
    }
  }
  return null;
}

/**
 * Map a WebAuthn ceremony error (from runAssertion /
 * runAttestation) to a HelpReason. The runner wraps
 * browser-layer errors into CeremonyError with a typed
 * code; we forward the ones the UI distinguishes and
 * collapse the rest into "failed".
 */
function classifyCeremonyError(err: unknown): HelpReason {
  if (err && typeof err === "object" && "code" in err) {
    const code = (err as { code: string }).code;
    if (code === "not_supported") return "unsupported";
    if (code === "user_cancelled") return "cancelled";
  }
  return "failed";
}

function errMessage(err: unknown, fallback: string): string {
  if (err && typeof err === "object" && "message" in err) {
    const msg = (err as { message: unknown }).message;
    if (typeof msg === "string" && msg.length > 0) return msg;
  }
  return fallback;
}
